// Central content for the Nara AI Agency marketing site.

export const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Work", href: "#case-studies" },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
  { label: "Journal", href: "#blog" },
  { label: "FAQ", href: "#faq" },
];

export const MARQUEE_WORDS = [
  "Efficiency Upgrades",
  "Automated Lead Responsiveness",
  "Intelligent Document Automation",
  "Automated Pipeline Nurturing",
  "Lost Customer Recovery",
  "Executive Dashboards",
];

export const SERVICES = [
  {
    no: "01",
    title: "Stopping the Bleeding First",
    desc: "Fix your broken internal operations before spending a single dollar on new ads.",
    span: "lg:col-span-3 lg:row-span-2",
    tone: "sage",
  },
  {
    no: "02",
    title: "Instant Customer Responses",
    desc: "Automatically texting or emailing a potential customer the exact second they fill out a form on your website.",
    span: "lg:col-span-3",
    tone: "peach",
  },
  {
    no: "03",
    title: "Killing the Paperwork",
    desc: "Letting our AI handle data entry, sorting incoming customer emails, reading attached PDFs, and filing estimates.",
    span: "lg:col-span-3",
    tone: "cream",
  },
  {
    no: "04",
    title: "Smart Client Follow-Ups",
    desc: "Setting up system reminders that automatically check in on sent quotes, ask for reviews after a job, or drop helpful tips to prospects over time.",
    span: "lg:col-span-2",
    tone: "apricot",
  },
  {
    no: "05",
    title: "Warming Up Old Leads",
    desc: "Sending a personalized text or email offer to your list of past customers or old leads who never bought from you.",
    span: "lg:col-span-2",
    tone: "cream",
  },
  {
    no: "06",
    title: "Performance Dashboards",
    desc: "A clean, automated scoreboard that shows you exactly where your jobs are coming from, what your profit margins look like, and how your team is doing.",
    span: "lg:col-span-2",
    tone: "peach",
  },
];

export const STEPS = [
  {
    no: "01",
    title: "Discovery",
    body: "We map the tasks eating your time and pick the one agent that pays for itself fastest.",
  },
  {
    no: "02",
    title: "Design",
    body: "We program the agent's logic and tools, training it directly on your actual tools.",
  },
  {
    no: "03",
    title: "Build & test",
    body: "We build, integrate with your tools, and stress-test against edge cases before anything goes live.",
  },
  {
    no: "04",
    title: "Launch & improve",
    body: "Your agent goes live. We monitor, refine, and expand it as your business grows.",
  },
];

export const CASE_STUDIES = [
  {
    client: "Maple & Co. Bakery",
    result: "68% fewer support emails",
    desc: "A support agent that handles orders, allergens and store hours across web and Instagram.",
    tag: "Support",
    img: "https://images.unsplash.com/photo-1758598497635-48cbbb1f6555?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzg0MzIwMzc4fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    client: "Northgate Dental",
    result: "2.4x more booked calls",
    desc: "A lead agent that qualifies new patients and books them straight into the calendar.",
    tag: "Sales",
    img: "https://images.unsplash.com/photo-1765648580808-76d75e4f3833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzg0MzIwMzc4fDA&ixlib=rb-4.1.0&q=85",
  },
];

export const PRICING = [
  {
    name: "Starter",
    tagline: "Perfect for businesses getting started with AI.",
    implFee: "$1,500 Implementation Fee",
    price: "$199",
    cadence: "/month",
    bestFor: ["Solopreneurs", "Small local businesses", "Businesses wanting to automate one process"],
    includes: [
      "1 Custom AI Employee",
      "Workflow Automation",
      "30 Days of Optimization",
      "Email Support",
      "Performance Monitoring",
      "Launch in 7–10 Days",
    ],
    featured: false,
  },
  {
    name: "Growth",
    tagline: "Build an AI team that works 24/7.",
    implFee: "$3,000 Implementation Fee",
    price: "$499",
    cadence: "/month",
    bestFor: ["Growing businesses", "Teams with multiple repetitive tasks", "Companies ready to scale"],
    includes: [
      "Up to 4 AI Employees",
      "Unlimited Integrations",
      "CRM Automation",
      "Email + SMS Automation",
      "Monthly AI Optimization",
      "Priority Support",
      "Quarterly Strategy Call",
    ],
    featured: true,
  },
  {
    name: "Enterprise",
    tagline: "Custom AI employees built around your business.",
    implFee: null,
    price: "Custom Pricing",
    cadence: "",
    bestFor: [],
    includes: [
      "Unlimited AI Employees",
      "Custom Dashboards",
      "API Development",
      "Database Integrations",
      "Internal Employee AI Tools",
      "Advanced Security",
      "Dedicated AI Engineer",
      "Quarterly Business Reviews",
      "Custom Training",
    ],
    featured: false,
  },
];

export const TESTIMONIALS = [
  {
    quote: "It genuinely feels like we hired three more people — except they never sleep and never complain.",
    name: "Elena Rossi",
    role: "Owner, Maple & Co.",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDIyODc0NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "Nara understood our clinic before writing a line of code. The booking agent paid for itself in a month.",
    name: "David Osei",
    role: "Practice Manager, Northgate",
    img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDIyODc0NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote: "Warm, fast and refreshingly non-techy. They speak human, not jargon.",
    name: "Priya Nair",
    role: "Founder, Loom Studio",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDIyODc0NXww&ixlib=rb-4.1.0&q=85",
  },
];

export const FAQS = [
  {
    q: "What is an AI employee?",
    a: "An AI employee is a custom AI assistant that works for your business 24/7. It can answer customer questions, qualify leads, schedule appointments, respond to emails, update your CRM, and automate repetitive tasks—so your team can focus on higher-value work.",
  },
  {
    q: "How long does it take to build my AI employee?",
    a: "Most projects are completed in 1–2 weeks. More complex systems with multiple integrations may take longer.",
  },
  {
    q: "Can the AI work with my current software?",
    a: "In many cases, yes. We can often integrate with popular tools such as:\n• Google Workspace\n• Microsoft 365\n• HubSpot\n• Salesforce\n• GoHighLevel\n• Calendly\n• QuickBooks\n• Slack\n• Zapier\n• n8n\n\nIf you're using another platform, we'll let you know whether it's supported.",
  },
  {
    q: "Will the AI replace my employees?",
    a: "No. Our goal is to automate repetitive work so your employees can spend more time serving customers, making sales, and growing the business.",
  },
  {
    q: "Can I request changes later?",
    a: "Yes. Businesses evolve, and your AI should too. We can add new workflows, integrations, or features as your needs change.",
  },
  {
    q: "Do you offer ongoing support?",
    a: "Yes. Every managed plan includes ongoing support and maintenance. Higher-tier plans include faster response times and more optimization.",
  },
  {
    q: "What if my business grows?",
    a: "That's exactly what our services are designed for. We can add additional AI employees, automate more departments, and expand your workflows as your business scales.",
  },
  {
    q: "How much does it cost?",
    a: "Our pricing depends on the complexity of your project. Most businesses pay a one-time implementation fee followed by a monthly support plan that keeps their AI running, updated, and optimized.",
  },
  {
    q: "What happens if something stops working?",
    a: "Just contact us. We'll investigate the issue, make any necessary fixes covered by your plan, and keep you informed throughout the process.",
  },
];

export const BLOG = [
  { date: "Dec 2025", cat: "Guide", title: "The small-business owner's guide to AI agents", read: "6 min" },
  { date: "Nov 2025", cat: "Playbook", title: "Five workflows worth automating first", read: "4 min" },
  { date: "Nov 2025", cat: "Story", title: "How a bakery cut support emails by two-thirds", read: "5 min" },
];

export const SERVICE_OPTIONS = [
  "Support Agents",
  "Lead & Sales Agents",
  "Ops Automation",
  "Inbox & Docs Copilots",
  "Custom Build",
  "Not sure yet",
];
